import { useEffect, useState } from 'react';
import { db } from '../../firebase';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';

/**
 * 팝업 문서 인터페이스 (popups 컬렉션)
 */
export interface PopupDoc {
  id: string;
  downloadUrl: string;
  title?: string;
  description?: string;
  linkUrl?: string;
  linkTarget?: '_blank' | '_self';
  isActive: boolean;
  order: number;
  startAt?: any;
  endAt?: any;
  createdAt?: any;
  updatedAt?: any;
}

// 하위 호환용 alias
export type ActivePopupDoc = PopupDoc;

/**
 * 활성 팝업 목록을 실시간 구독하는 훅
 * - popups 컬렉션에서 isActive, 시간 조건을 만족하는 문서를 order 순으로 반환
 */
export function useActivePopup() {
  const [popups, setPopups] = useState<PopupDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, 'popups'), orderBy('order', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setLoading(false);
        const now = Date.now();
        const docs: PopupDoc[] = [];
        snap.forEach((doc) => {
          const d = { id: doc.id, ...doc.data() } as PopupDoc;
          if (!d.isActive) return;
          // startAt 검사
          if (d.startAt) {
            const t = d.startAt?.toMillis ? d.startAt.toMillis() : new Date(d.startAt).getTime();
            if (now < t) return;
          }
          // endAt 검사
          if (d.endAt) {
            const t = d.endAt?.toMillis ? d.endAt.toMillis() : new Date(d.endAt).getTime();
            if (now > t) return;
          }
          docs.push(d);
        });
        setPopups(docs);
      },
      (err) => {
        setLoading(false);
        setError(err?.message || '팝업 구독 실패');
      }
    );
    return () => unsub();
  }, []);

  // 하위 호환: 첫 번째 팝업을 data로 반환
  const data = popups.length > 0 ? popups[0] : null;

  return {
    data,
    popups,
    url: data?.downloadUrl || '',
    loading,
    error,
    shouldShowPopup: popups.length > 0,
  };
}

/**
 * "오늘 하루 보지 않기" 로컬스토리지 유틸
 */
const DISMISS_KEY = 'popup_dismissed_global';

export function getDismissedKey(popupId: string): string {
  return `popup_dismissed_${popupId}`;
}

export function isPopupDismissedToday(_popupId?: string): boolean {
  const dismissedUntil = localStorage.getItem(DISMISS_KEY);
  if (!dismissedUntil) return false;
  return parseInt(dismissedUntil, 10) > Date.now();
}

export function setPopupDismissedToday(_popupId?: string): void {
  const now = new Date();
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  localStorage.setItem(DISMISS_KEY, midnight.getTime().toString());
}
